import type { PartnerEngagementSummary, PostContactRequest } from '@houzy/schemas/partner-cockpit/partner-matching';
import {
    useMutation,
    useQuery,
    useQueryClient,
    queryOptions,
    type UseMutationResult,
    type UseQueryOptions,
    type UseQueryResult,
} from '@tanstack/react-query';

import { fetchMatchingEngagement, postContactRequest } from './matchingApi';
import { offerRequestDetailQueryKey } from './useOfferRequestDetail';
import { offerRequestsSegmentKey } from './useOfferRequestsList';

export function matchingEngagementQueryKey(
    matchingUuid: string,
): readonly ['offerRequests', 'matching-engagement', string] {
    return ['offerRequests', 'matching-engagement', matchingUuid] as const;
}

type MatchingEngagementQueryKey = ReturnType<typeof matchingEngagementQueryKey>;

export function matchingEngagementQueryOptions(
    matchingUuid: string,
): UseQueryOptions<
    Awaited<ReturnType<typeof fetchMatchingEngagement>>,
    Error,
    Awaited<ReturnType<typeof fetchMatchingEngagement>>,
    MatchingEngagementQueryKey
> {
    return queryOptions({
        queryKey: matchingEngagementQueryKey(matchingUuid),
        queryFn: ({ signal }) => fetchMatchingEngagement(matchingUuid, signal),
    });
}

export function useMatchingEngagement(matchingUuid: string | null): UseQueryResult<PartnerEngagementSummary | null> {
    return useQuery({
        ...matchingEngagementQueryOptions(matchingUuid ?? ''),
        enabled: matchingUuid !== null,
    });
}

export function usePostContactRequest(
    matchingUuid: string | null,
): UseMutationResult<unknown, Error, PostContactRequest> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: PostContactRequest) => {
            if (matchingUuid === null) {
                return Promise.reject(new Error('No match to send the contact request to'));
            }
            return postContactRequest(matchingUuid, payload);
        },
        // Returned, not fired and forgotten, so `mutateAsync` resolves only once the refreshed lead has landed.
        onSuccess: () =>
            Promise.all([
                queryClient.invalidateQueries({
                    queryKey: matchingEngagementQueryKey(matchingUuid ?? ''),
                    exact: true,
                }),
                queryClient.invalidateQueries({
                    queryKey: offerRequestDetailQueryKey(matchingUuid ?? ''),
                    exact: true,
                }),
                queryClient.invalidateQueries({ queryKey: offerRequestsSegmentKey('new') }),
                queryClient.invalidateQueries({ queryKey: offerRequestsSegmentKey('open') }),
            ]),
    });
}
